<script setup lang="ts">
/**
 * 内联控件分发器。
 * 按 widget.kind 选择具体控件，并把 `update` 事件统一成
 * `(widgetId, value)` 向上抛，方便宿主写一处状态更新逻辑。
 *
 * ⚠️ **`ports`（端口声明表）在这里没有分支**：它已经不在节点上渲染
 * （那一行是 `panelOnly`，编辑在「属性」面板里做）。值照样存在
 * `row.widget.value` 里 —— 它只是没有内联形态。
 */
import type { Widget, WidgetValue } from '../../types/widget'
import NpWidgetColor from './NpWidgetColor.vue'
/*
 * ⚠️ 图像控件是 `.ts`（不是 `.vue`）：包的 `panels.ts` 要直接复用它，
 * 而纯逻辑验证脚本用 esbuild 打包、不认 `.vue`（同 `NpPanelCard.ts`）。
 */
import { NpWidgetImage } from './NpWidgetImage'
import NpWidgetText from './NpWidgetText.vue'
import NpWidgetCode from './NpWidgetCode.vue'
import NpWidgetNumber from './NpWidgetNumber.vue'
import NpWidgetBoolean from './NpWidgetBoolean.vue'
import NpWidgetEnum from './NpWidgetEnum.vue'
import NpWidgetCustom from './NpWidgetCustom.vue'

const props = defineProps<{ model: Widget }>()
const emit = defineEmits<{
  (e: 'update', id: string, value: WidgetValue): void
  (e: 'resize', id: string, height: number): void
}>()

function update(value: WidgetValue) {
  emit('update', props.model.id, value)
}

function resize(height: number) {
  emit('resize', props.model.id, height)
}
</script>

<template>
  <NpWidgetColor
    v-if="model.kind === 'color'"
    :widget="model"
    @update="update"
  />
  <NpWidgetImage
    v-else-if="model.kind === 'image'"
    :widget="model"
    @update="update"
  />

  <NpWidgetCode
    v-else-if="model.kind === 'code'"
    :widget="model"
    @update="update"
    @resize="resize"
  />

  <!-- ⚠️ `ports` 没有分支：它只在属性面板里编辑（见文件头那段说明） -->

  <NpWidgetText
    v-else-if="model.kind === 'text'"
    :widget="model"
    @update="update"
    @resize="resize"
  />

  <NpWidgetNumber
    v-else-if="model.kind === 'number'"
    :widget="model"
    @update="update"
  />

  <NpWidgetBoolean
    v-else-if="model.kind === 'boolean'"
    :widget="model"
    @update="update"
  />

  <NpWidgetEnum
    v-else-if="model.kind === 'enum'"
    :widget="model"
    @update="update"
  />

  <NpWidgetCustom
    v-else-if="model.kind === 'custom'"
    :widget="model"
  >
    <template #default="slotProps">
      <slot name="custom" v-bind="slotProps" />
    </template>
  </NpWidgetCustom>
</template>
